import React, { useMemo, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowUpDown, Download, PlusCircle, Search, AlertTriangle } from "lucide-react";
import { LowStockProduct } from "@/types/dashboard-type";
import { cn } from "@/lib/utils";

type Props = {
    products: LowStockProduct[];
    loading?: boolean;
    minHeight?: string;
    maxHeight?: string;
    title?: string;
    onRowClick?: (p: LowStockProduct) => void;
    onRestock?: (p: LowStockProduct) => void;
    onExportCsv?: () => void;
};

export const LowStockProductsCard: React.FC<Props> = ({
    products,
    loading = false,
    minHeight = "260px",
    maxHeight = "420px",
    title = "Low Stock Products",
    onRowClick,
    onRestock,
    onExportCsv,
}) => {
    const [q, setQ] = useState("");
    const [sortAsc, setSortAsc] = useState(true);

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        const base = products.filter((p) =>
            !needle ||
            p.sku.toLowerCase().includes(needle) ||
            p.productName.toLowerCase().includes(needle)
        );

        // Deduplicate by variantId + productId to avoid key collisions
        const seen = new Set();
        const unique = base.filter((p) => {
            const key = `${p.variantId}-${p.productId}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });

        return unique.sort((a, b) =>
            sortAsc ? a.openQty - b.openQty : b.openQty - a.openQty
        );
    }, [products, q, sortAsc]);

    return (
        <Card className="xl:col-span-5 h-full flex flex-col">
            <CardHeader className="pb-4 border-b">
                <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-base font-semibold flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 text-amber-500" />
                            {title}
                        </CardTitle>
                        {onExportCsv && (
                            <Button variant="ghost" size="sm" onClick={onExportCsv} className="h-8 px-2">
                                <Download className="h-4 w-4 mr-1" />
                                <span className="text-xs">Export</span>
                            </Button>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search products..."
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                className="pl-9 h-9"
                            />
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-9 px-2"
                            onClick={() => setSortAsc((s) => !s)}
                        >
                            <ArrowUpDown className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </CardHeader>

            <CardContent className="p-0">
                <div
                    className="overflow-auto bg-white"
                    style={{ minHeight, maxHeight }}
                >
                    <Table>
                        <TableHeader className="bg-muted/50 sticky top-0 z-10">
                            <TableRow>
                                <TableHead className="text-[11px] uppercase tracking-wider">Product Info</TableHead>
                                <TableHead className="hidden md:table-cell text-right text-[11px] uppercase">Sold (7d)</TableHead>
                                <TableHead className="text-right text-[11px] uppercase">Stock</TableHead>
                                <TableHead className="text-right w-[60px]"></TableHead>
                            </TableRow>
                        </TableHeader>

                        <TableBody>
                            {!loading && filtered.map((p: LowStockProduct) => {
                                const isCritical = p.openQty <= 0;

                                return (
                                    <TableRow
                                        key={`${p.variantId}-${p.productId}-${p.sku}`}
                                        className={cn(
                                            "hover:bg-muted/30 cursor-pointer transition-colors",
                                            isCritical && "bg-red-50/50 hover:bg-red-50"
                                        )}
                                        onClick={() => onRowClick?.(p)}
                                    >
                                        <TableCell className="py-3">
                                            <div className="flex flex-col gap-0.5">
                                                <span className="text-xs font-mono text-muted-foreground">
                                                    {p.sku}
                                                </span>
                                                <span className="text-sm font-semibold line-clamp-1">
                                                    {p.productName}
                                                </span>
                                                {/* Show sales summary on mobile under name */}
                                                <span className="md:hidden text-[10px] text-muted-foreground">
                                                    Sold: {p.totalQtySold ?? 0} in last 7 days
                                                </span>
                                            </div>
                                        </TableCell>

                                        {/* Desktop Only */}
                                        <TableCell className="hidden md:table-cell text-right text-sm">
                                            {p.totalQtySold ?? 0}
                                        </TableCell>

                                        <TableCell className="text-right">
                                            <div className="flex flex-col items-end">
                                                <span className={cn(
                                                    "text-sm font-bold",
                                                    isCritical ? "text-red-600" : "text-amber-600"
                                                )}>
                                                    {p.openQty}
                                                </span>
                                                <span className="text-[10px] uppercase text-muted-foreground font-medium">
                                                    In Hand
                                                </span>
                                            </div>
                                        </TableCell>

                                        <TableCell className="text-right pr-4">
                                            {onRestock && (
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-indigo-600 hover:bg-indigo-50"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onRestock(p);
                                                    }}
                                                >
                                                    <PlusCircle className="h-5 w-5" />
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}

                            {!loading && filtered.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} className="h-32 text-center text-muted-foreground">
                                        All products are well stocked.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>
        </Card>
    );
};